import {
  MetricsV1ExpressionInput,
  MetricsV1FilterCriteriaEntryInput,
  MetricsV1FilterCriterionAttributeCriterionInput,
  MetricsV1FilterInput,
  MetricsV1FilterValueInput,
} from '@spotify/confidence-graphql';
import { NormalizedExpression } from '@spotify/plugin-confidence-flags-react';

const isNullCheck = (value: MetricsV1FilterValueInput): boolean =>
  value.nullValue !== undefined && value.nullValue !== null;

const normalizeValue = (
  value: MetricsV1FilterValueInput,
): NormalizedExpression.Value => {
  if (isNullCheck(value)) {
    return null;
  }

  if (value.stringValue !== undefined && value.stringValue !== null) {
    return value.stringValue;
  }

  if (value.timestampValue !== undefined && value.timestampValue !== null) {
    return value.timestampValue;
  }

  if (value.numberValue !== undefined && value.numberValue !== null) {
    return value.numberValue;
  }

  if (value.boolValue !== undefined && value.boolValue !== null) {
    return value.boolValue;
  }

  return null;
};

const normalizeCriterion = (
  criterion: MetricsV1FilterCriterionAttributeCriterionInput,
  isNot: boolean,
): NormalizedExpression.Expression => {
  const attributeName = criterion.attribute;

  // Handle eqRule
  if (criterion.eqRule) {
    // Check if this is a null check
    if (isNullCheck(criterion.eqRule.value)) {
      return {
        type: 'unary',
        attributeName,
        operator: isNot ? 'is-not-null' : 'is-null',
      };
    }

    // Regular equality check
    return {
      type: 'binary',
      attributeName,
      operator: isNot ? '!=' : '=',
      value: normalizeValue(criterion.eqRule.value),
    };
  }

  // Handle setRule
  if (criterion.setRule) {
    return {
      type: 'set',
      attributeName,
      operator: isNot ? 'none-of' : 'any-of',
      values: criterion.setRule.values.map(v => normalizeValue(v)),
    };
  }

  // Handle rangeRule
  if (criterion.rangeRule) {
    const { startExclusive, startInclusive, endExclusive, endInclusive } =
      criterion.rangeRule;

    const hasStart = startExclusive || startInclusive;
    const hasEnd = endExclusive || endInclusive;

    // Two bounds -> range expression (between)
    if (hasStart && hasEnd) {
      const startValue = startExclusive || startInclusive;
      const endValue = endExclusive || endInclusive;

      return {
        type: 'range',
        attributeName,
        operator: isNot ? 'not-between' : 'between',
        range: {
          start: normalizeValue(startValue!),
          end: normalizeValue(endValue!),
          startInclusive: !!startInclusive,
          endInclusive: !!endInclusive,
        },
      };
    }

    // Single bound -> binary expression
    if (startExclusive) {
      return {
        type: 'binary',
        attributeName,
        operator: isNot ? '<=' : '>',
        value: normalizeValue(startExclusive),
      };
    }
    if (startInclusive) {
      return {
        type: 'binary',
        attributeName,
        operator: isNot ? '<' : '>=',
        value: normalizeValue(startInclusive),
      };
    }
    if (endExclusive) {
      return {
        type: 'binary',
        attributeName,
        operator: isNot ? '>=' : '<',
        value: normalizeValue(endExclusive),
      };
    }
    if (endInclusive) {
      return {
        type: 'binary',
        attributeName,
        operator: isNot ? '>' : '<=',
        value: normalizeValue(endInclusive),
      };
    }
  }

  // Handle likeRule
  if (criterion.likeRule) {
    return {
      type: 'like',
      attributeName,
      operator: isNot ? 'not-like' : 'like',
      pattern: criterion.likeRule.pattern,
    };
  }

  // Fallback: return an "is null" unary expression for unknown/invalid criteria
  return {
    type: 'unary',
    attributeName,
    operator: 'is-null',
  };
};

const normalizeLogicalExpression = (
  operands: MetricsV1ExpressionInput[],
  operator: 'and' | 'or',
  criteriaMap: Map<string, MetricsV1FilterCriteriaEntryInput>,
): NormalizedExpression.Expression | null => {
  const normalizedOperands = operands
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    .map(op => normalizeExpression(op, criteriaMap))
    .filter((op): op is NormalizedExpression.Expression => op !== null);

  if (normalizedOperands.length === 0) return null;
  if (normalizedOperands.length === 1) return normalizedOperands[0];

  return {
    type: 'logical',
    operator,
    operands: normalizedOperands,
  };
};

const normalizeExpression = (
  expr: MetricsV1ExpressionInput,
  criteriaMap: Map<string, MetricsV1FilterCriteriaEntryInput>,
): NormalizedExpression.Expression | null => {
  // Handle AND expression
  if (expr.and?.operands) {
    return normalizeLogicalExpression(expr.and.operands, 'and', criteriaMap);
  }

  // Handle OR expression
  if (expr.or?.operands) {
    return normalizeLogicalExpression(expr.or.operands, 'or', criteriaMap);
  }

  // Handle NOT expression (wraps a ref)
  if (expr.not?.ref) {
    const criterion = criteriaMap.get(expr.not.ref);
    if (!criterion?.value?.attribute) return null;
    return normalizeCriterion(criterion.value.attribute, true);
  }

  // Handle ref (direct reference to a criterion)
  if (expr.ref) {
    const criterion = criteriaMap.get(expr.ref);
    if (!criterion?.value?.attribute) return null;
    return normalizeCriterion(criterion.value.attribute, false);
  }

  return null;
};

export const normalizeFilterInput = (
  filter: MetricsV1FilterInput,
): NormalizedExpression.Expression | null => {
  if (!filter.criteria || filter.criteria.length === 0) {
    return null;
  }

  // Build a map of criteria by key for efficient lookup
  const criteriaMap = new Map<string, MetricsV1FilterCriteriaEntryInput>();
  for (const criterion of filter.criteria) {
    criteriaMap.set(criterion.key, criterion);
  }

  return normalizeExpression(filter.expression, criteriaMap);
};
